import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
    ArrowSyncFilled,
    ArrowSyncRegular,
    ArrowTrendingLinesFilled,
    ArrowTrendingLinesRegular,
    bundleIcon,
    CalendarAddFilled,
    CalendarAddRegular,
    CalendarDayRegular,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    EditFilled,
    EditRegular,
    FlowchartRegular,
    InfoFilled,
    InfoRegular,
    MailAddFilled,
    MailAddRegular,
    MoneyRegular,
    PrintFilled,
    PrintRegular,
} from '@fluentui/react-icons'
import { Button, Card, makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger, Text, tokens, Tooltip } from '@fluentui/react-components'
import { DocumentGenerationParams, DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import useAxios from '../../../hooks/useAxios'
import { useDispatch } from 'react-redux'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useSharePoint from '../../../hooks/useSharePoint'
import { DRIVE_ITEM_CONFIG, getFullPath } from '../../../components/TextField/Folder/FolderField'
import { IGraphResponse } from '../../../context/AxiosContext'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import PersonAvatar from '../../../components/PersonAvatar'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import { toast } from 'react-toastify'
import { SharePointIcon } from '../../../components/Icon/SharePointIcon'
import OverflowToolbarPanel, { HammockToolbarButtonData } from '../../../components/Overflow/OverflowToolbarPanel'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import { FileRateType, ModuleType } from '../../../context/GlobalContext'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../hooks/file/useFilePages'
import useCurrency from '../../../hooks/useCurrency'
import useWorkflows from '../../../hooks/useWorkflows'
import { FileAction } from '../../../config/fileConfig'
import HammockDialog from '../../components/Dialog/HammockDialog'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import IconValue from '../../../components/IconValue/IconValueV9'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import { formatFullDate } from '../../../helpers/dateHelper'
import { FileRouteMatchProps, usePageStyles, ViewFileProps } from './ViewFile'
import ComposeEmailDialogBody, { ComposeEmailDialogBodyProps } from '../../components/Dialog/OutlookItem/ComposeEmailDialogBody'
import ComposeAppointmentDialogBody, { ComposeAppointmentDialogBodyProps } from '../../components/Dialog/OutlookItem/ComposeAppointmentDialogBody'
import { toastContainerId, useApplicationStyles } from '../../../pages/App'
import { TimerAddIcon } from '../../../components/Icon/TimerAddIcon'

const ArrowSyncIcon = bundleIcon(ArrowSyncFilled, ArrowSyncRegular)
const ArrowTrendingLinesIcon = bundleIcon(ArrowTrendingLinesFilled, ArrowTrendingLinesRegular)
const CalendarAddIcon = bundleIcon(CalendarAddFilled, CalendarAddRegular)
const ClipboardTaskAddIcon = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)
const EditIcon = bundleIcon(EditFilled, EditRegular)
const InfoIcon = bundleIcon(InfoFilled, InfoRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)
const PrintIcon = bundleIcon(PrintFilled, PrintRegular)

const useStyles = makeStyles({
    flexLine: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
        minHeight: '32px',
    },
    gridLine: {
        display: 'grid',
        gridTemplateColumns: '28px max-content auto 28px',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
        minHeight: '32px',
    },
    narrowGridLine: {
        gridTemplateColumns: 'max-content auto 28px',
    },
    lineSpaceBetween: {
        justifyContent: 'space-between',
    },
    lineSpaceBetweenExpand: {
        display: 'flex',
        width: '32px',
    },
    lineWrap: {
        flexWrap: 'wrap',
        alignItems: 'flex-start',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: '55px auto',
        gap: tokens.spacingHorizontalS,
    },
})

type FileCardParamsDto = DocumentGenerationParams<{
    fileId: string
}>

export default function ViewFileHeader(props: ViewFileProps): ReactElement {
    const appStyles = useApplicationStyles()
    const axios = useAxios()
    const dispatch = useDispatch()
    const history = useHistory()
    const modules = useModules()
    const pageStyles = usePageStyles()
    const roles = useRoles()
    const { sharePoint } = useSharePoint()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { getWorkflowById } = useWorkflows()

    const viewportNarrow = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const submitRequest = useSubmitDocumentGenerationRequest<FileCardParamsDto>(DocumentGenerationType.FileCard)

    const { currency, currencyName } = useCurrency(props.file.currency)
    const routeMatch = useRouteMatch<FileRouteMatchProps>()

    const [currentDocumentRootUrl, setCurrentDocumentRootUrl] = useState<string>()
    useEffect(() => {
        if (sharePoint?.list?.webUrl && props.file.documentRootId) {
            axios.get(`/items/${props.file.documentRootId}`, DRIVE_ITEM_CONFIG).then((response: IGraphResponse<DriveItem>) => {
                if (response.data) {
                    setCurrentDocumentRootUrl(sharePoint?.list?.webUrl + '/' + encodeURIComponent(getFullPath(response.data)))
                }
            })
        }
    }, [sharePoint])

    const taskOnClick = useCallback(
        () =>
            history.push(generatePath(`${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`, routeMatch.params), {
                attachmentIds: props.selectedDriveItemIds ?? [],
            }),
        [props.selectedDriveItemIds],
    )

    const copyToSubjectClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.subject?.getAsync) {
            Office.context.mailbox.item?.subject?.getAsync(result => {
                if (result.value.toLowerCase().indexOf(value.toLowerCase()) === -1) {
                    Office.context.mailbox.item?.subject.setAsync(`${result.value} ${value}`)
                }
            })
        } else {
            navigator.clipboard.writeText(`${value}`)
            toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
        }
    }

    const copyToBodyClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.body?.prependAsync) {
            Office.context.mailbox.item?.body.prependAsync(value)
        } else {
            navigator.clipboard.writeText(`${value}`)
            toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
        }
    }

    const buttons = useMemo<HammockToolbarButtonData[]>(
        (): HammockToolbarButtonData[] =>
            [
                {
                    value: 'time',
                    icon: <TimerAddIcon />,
                    titleKey: 'action.create.time.title',
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                    onClick: () => history.push(generatePath(`${fileRoutePrefix}/time/add`, routeMatch.params)),
                },
                {
                    value: 'task',
                    icon: <ClipboardTaskAddIcon />,
                    titleKey: 'action.create.task.title',
                    filter: () => modules.any([ModuleType.Tasks, ModuleType.Workflow]) && roles.any(rolesTasks),
                    onClick: taskOnClick,
                },
                {
                    value: 'email',
                    icon: <MailAddIcon />,
                    titleKey: 'action.create.email',
                    filter: () => !isAppContext(AppContext.Compose),
                    render: (button, key) => (
                        <HammockDialog<ComposeEmailDialogBodyProps>
                            tooltipProps={{ content: translate('action.create.email') }}
                            triggerComponent={() => <Button key={key} appearance={'subtle'} disabled={button.disabled} icon={button.icon} />}
                            dialogBody={ComposeEmailDialogBody}
                            dialogBodyProps={() => props}
                        />
                    ),
                },
                {
                    value: 'appointment',
                    icon: <CalendarAddIcon />,
                    titleKey: 'action.create.appointment',
                    filter: () => isOfficeInitialized() && !isAppContext(AppContext.Compose),
                    render: (button, key) => (
                        <HammockDialog<ComposeAppointmentDialogBodyProps>
                            tooltipProps={{ content: translate('action.create.appointment') }}
                            triggerComponent={() => <Button key={key} appearance={'subtle'} disabled={button.disabled} icon={button.icon} />}
                            dialogBody={ComposeAppointmentDialogBody}
                            dialogBodyProps={() => ({ file: props.file, parties: props.parties })}
                        />
                    ),
                },
                {
                    value: 'invoice',
                    icon: <ArrowTrendingLinesIcon />,
                    titleKey: 'action.create.invoice.title',
                    filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                    onClick: () => history.push(`/invoice/add`, { fileIds: [props.fileId] }),
                },
                {
                    value: 'print',
                    icon: <PrintIcon />,
                    titleKey: 'file.pdf',
                    filter: () => !!props.file.documentRootId,
                    onClick: () => submitRequest({ fileId: props.fileId }),
                },
                {
                    value: 'refresh',
                    icon: <ArrowSyncIcon />,
                    titleKey: 'file.refresh',
                    filter: () => roles.none([Role.FileLimited]),
                    onClick: () => {
                        dispatch({ type: FileAction.ResetCurrent })
                        props.resetFile()
                    },
                },
                {
                    value: 'sharepoint',
                    icon: <SharePointIcon />,
                    titleKey: 'menu.sharepoint',
                    filter: () => !!props.file.documentRootId,
                    onClick: () => window.open(currentDocumentRootUrl, '_blank'),
                },
            ].filter((item: HammockToolbarButtonData) => (item.filter ? item.filter() : true)),
        [roles.loaded, modules.loaded, currentDocumentRootUrl, props.selectedDriveItemIds],
    )

    return (
        <Card className={mergeClasses(pageStyles.card, pageStyles.headerCard)}>
            <div className={mergeClasses(styles.gridLine, viewportNarrow && styles.narrowGridLine, appStyles.width100)}>
                {!viewportNarrow && <PersonAvatar name={props.file.owner?.fullName} color="colorful" size={24} />}
                <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                    <Text weight={'semibold'} onClick={() => copyToSubjectClick(`[${props.file.reference}]`)}>
                        {props.file.reference}
                    </Text>
                </Tooltip>
                <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow positioning={'below'}>
                    <Text className={appStyles.textNoWrapOverflowHidden} onClick={() => copyToSubjectClick(props.file.name)}>
                        {props.file.name}
                    </Text>
                </Tooltip>
                <div>
                    <Popover withArrow positioning={'below-end'}>
                        <PopoverTrigger disableButtonEnhancement>
                            <Button icon={<InfoIcon />} appearance={'transparent'} />
                        </PopoverTrigger>
                        <PopoverSurface tabIndex={-1}>
                            <div className={styles.infoGrid}>
                                <Text>{translate('base.table.header.number')}</Text>
                                <Text>{props.file.reference}</Text>
                                <Text>{translate('base.table.header.name')}</Text>
                                <Text>{props.file.name}</Text>
                                <Text>{translate('file.edit.form.header.owner')}</Text>
                                <Text>{props.file.owner.fullName}</Text>
                                {props.file.description && (
                                    <>
                                        <Text>{translate('base.table.header.description.short')}</Text>
                                        <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                                            <Text onClick={() => copyToBodyClick(props.file.description)}>{props.file.description}</Text>
                                        </Tooltip>
                                    </>
                                )}
                                <Text>{translate('base.table.header.rate')}</Text>
                                <IconValue icon={<MoneyRegular />}>
                                    {props.file.fileRateType === FileRateType.File ? (
                                        <MoneyText amount={props.file.rate} currency={currency} />
                                    ) : props.file.fileRateType === FileRateType.UserTier ? (
                                        `${translate('file.rate.usertier.label')}, ${currencyName} (${currency.symbol})`
                                    ) : (
                                        `${translate('file.rate.partytier.label')}, ${currencyName} (${currency.symbol})`
                                    )}
                                </IconValue>
                                {modules.has(ModuleType.Workflow) && props.file.workflows?.length > 0 && (
                                    <>
                                        <Text>{translate('file.module.workflow')}</Text>
                                        <IconValue icon={<FlowchartRegular />}>
                                            <Text>
                                                {(props.file.workflows || [])
                                                    .map(workflow => getWorkflowById(workflow)?.name)
                                                    .sort()
                                                    .join(', ')}
                                            </Text>
                                        </IconValue>
                                    </>
                                )}
                                {props.file.dateOfIncident && (
                                    <>
                                        <Text>{translate('file.edit.form.general.dateincident')}</Text>
                                        <IconValue icon={<CalendarDayRegular />}>
                                            <Text>{formatFullDate(props.file.dateOfIncident)}</Text>
                                        </IconValue>
                                    </>
                                )}
                            </div>
                        </PopoverSurface>
                    </Popover>
                </div>
            </div>
            <div className={mergeClasses(styles.flexLine, styles.lineSpaceBetween)}>
                <OverflowToolbarPanel
                    toolbarProps={{ className: pageStyles.toolbar }}
                    getToolbarButtonProps={() => ({
                        appearance: 'subtle',
                    })}
                    containerClassName={mergeClasses(styles.flexLine, pageStyles.toolbar)}
                    buttons={buttons}
                    overflowButtonLabelKey={'base.item.label'}
                />
                <div className={mergeClasses(styles.flexLine, styles.lineSpaceBetweenExpand)}>
                    <Tooltip content={translate('file.edit.action')} relationship={'description'} withArrow>
                        <Button icon={<EditIcon />} appearance={'subtle'} onClick={() => history.push(`/file/${props.fileId}/edit`)} />
                    </Tooltip>
                </div>
            </div>
        </Card>
    )
}
